import { useEffect, useState } from 'react';
import { api, searchAddress, type AddressSuggestion } from '../../lib/api';
import { euro } from '../../lib/format';
import { useToasts } from '../../lib/toast';
import type { DeliveryConfig, DeliveryQuoteResult } from '@taj/types';

/** Champ d'adresse avec autocomplétion (Base Adresse Nationale). */
export function AddressAutocomplete({ value, onPick, placeholder }: { value: string; onPick: (s: AddressSuggestion) => void; placeholder?: string }) {
  const [q, setQ] = useState(value);
  const [sug, setSug] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { setQ(value); }, [value]);
  useEffect(() => {
    if (q.trim().length < 3) { setSug([]); return; }
    const id = setTimeout(async () => { setSug(await searchAddress(q)); setOpen(true); }, 300);
    return () => clearTimeout(id);
  }, [q]);
  return (
    <div style={{ position: 'relative' }}>
      <input className="inp" style={{ width: '100%' }} placeholder={placeholder} value={q}
        onChange={(e) => setQ(e.target.value)} onFocus={() => sug.length && setOpen(true)} />
      {open && sug.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: 8, zIndex: 20, maxHeight: 230, overflow: 'auto', boxShadow: '0 10px 24px rgba(0,0,0,.15)' }}>
          {sug.map((s, i) => (
            <div key={i} style={{ padding: '9px 11px', cursor: 'pointer', borderBottom: '1px solid #f2f2f2' }}
              onClick={() => { onPick(s); setQ(s.label); setOpen(false); }}>📍 {s.label}</div>
          ))}
        </div>
      )}
    </div>
  );
}

const eurToCents = (s: string) => Math.round((parseFloat(s.replace(',', '.')) || 0) * 100);
const centsToEur = (c: number | null | undefined) => (c == null ? '' : (c / 100).toFixed(2));

export function DeliveryManager({ onBack }: { onBack: () => void }) {
  const push = useToasts((s) => s.push);
  const [cfg, setCfg] = useState<DeliveryConfig | null>(null);
  const [fee, setFee] = useState('0');
  const [free, setFree] = useState('');
  const [min, setMin] = useState('0');
  const [maxKm, setMaxKm] = useState('10');
  const [zones, setZones] = useState<{ km: string; fee: string; min: string; free: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [test, setTest] = useState<DeliveryQuoteResult | null>(null);

  useEffect(() => {
    api.getDeliveryConfig().then((c) => {
      setCfg(c);
      setFee(centsToEur(c.feeCents) || '0');
      setFree(centsToEur(c.freeAboveCents));
      setMin(centsToEur(c.minOrderCents) || '0');
      setMaxKm(String(c.maxKm));
      setZones((c.zones ?? []).map((z) => ({ km: String(z.maxKm), fee: centsToEur(z.feeCents) || '0', min: centsToEur(z.minOrderCents), free: centsToEur(z.freeAboveCents) })));
    }).catch((e) => push(e.message, 'warn'));
  }, [push]);

  if (!cfg) return <div className="no-data">Chargement…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.updateDeliveryConfig({
        enabled: cfg.enabled,
        originLabel: cfg.originLabel,
        originLat: cfg.originLat,
        originLon: cfg.originLon,
        feeCents: eurToCents(fee),
        freeAboveCents: free.trim() ? eurToCents(free) : null,
        minOrderCents: eurToCents(min),
        maxKm: parseFloat(maxKm.replace(',', '.')) || 10,
        zones: zones
          .map((z) => ({
            maxKm: parseFloat(z.km.replace(',', '.')) || 0,
            feeCents: eurToCents(z.fee),
            minOrderCents: z.min.trim() ? eurToCents(z.min) : undefined,
            freeAboveCents: z.free.trim() ? eurToCents(z.free) : undefined,
          }))
          .filter((z) => z.maxKm > 0),
      });
      setCfg(updated);
      push('✅ Livraison enregistrée');
    } catch (e: any) { push(e.message, 'warn'); }
    finally { setSaving(false); }
  };

  const testAddress = async (s: AddressSuggestion) => {
    try { setTest(await api.deliveryQuote(s.lat, s.lon)); }
    catch (e: any) { push(e.message, 'warn'); }
  };

  const field = (label: string, node: React.ReactNode) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: '.72rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>{label}</div>
      {node}
    </div>
  );

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>🛵 Livraison</div>
        <div className="al-toggle" style={{ padding: '6px 10px', margin: 0, gap: 8 }}>
          <span className="an" style={{ fontSize: '.78rem' }}>{cfg.enabled ? 'Activée' : 'Désactivée'}</span>
          <div className={`al-sw${cfg.enabled ? ' on' : ''}`} onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })} />
        </div>
      </div>

      <div className="cc" style={{ marginBottom: 16, maxWidth: 560 }}>
        {field('📍 Adresse du restaurant (point de départ)',
          <AddressAutocomplete
            value={cfg.originLabel ?? ''}
            placeholder="Tape l'adresse du restaurant…"
            onPick={(s) => setCfg({ ...cfg, originLabel: s.label, originLat: s.lat, originLon: s.lon })}
          />)}
        {cfg.originLat != null && <div style={{ color: '#2a7', fontSize: '.8rem', marginTop: -6, marginBottom: 12 }}>✅ Position enregistrée</div>}

        <div style={{ display: 'flex', gap: 12 }}>
          {field('Frais de livraison (€)', <input className="inp" inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} />)}
          {field('Gratuit au-dessus de (€)', <input className="inp" inputMode="decimal" placeholder="ex. 30" value={free} onChange={(e) => setFree(e.target.value)} />)}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {field('Minimum de commande (€)', <input className="inp" inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} />)}
          {field('Rayon de livraison max (km)', <input className="inp" inputMode="decimal" value={maxKm} onChange={(e) => setMaxKm(e.target.value)} />)}
        </div>
        {field('🗺️ Zones de livraison (par distance)',
          <div>
            <div style={{ fontSize: '.78rem', color: 'var(--ink-faint)', marginBottom: 8 }}>
              {zones.length === 0
                ? "Aucune zone : le tarif unique ci-dessus s'applique jusqu'au rayon max."
                : 'Chaque zone va de la distance précédente jusqu’à sa distance max. Au-delà de la dernière zone : livraison refusée.'}
            </div>
            {zones.map((z, i) => {
              const low = i === 0 ? '0' : (zones[i - 1].km || '?');
              const setZ = (patch: Partial<{ km: string; fee: string; min: string; free: string }>) =>
                setZones(zones.map((x, j) => (j === i ? { ...x, ...patch } : x)));
              const miniField = (label: string, node: React.ReactNode) => (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '.68rem', color: 'var(--ink-faint)', fontWeight: 700 }}>{label}{node}</label>
              );
              return (
                <div key={i} style={{ border: '1px solid var(--gold-line)', borderRadius: 10, padding: 10, marginBottom: 8, background: 'var(--paper)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontWeight: 800, fontSize: '.85rem', color: 'var(--gold)' }}>Zone {i + 1}</span>
                    <span style={{ fontSize: '.78rem', color: 'var(--ink-dim)' }}>de {low} km à</span>
                    <input className="inp" inputMode="decimal" style={{ width: 64 }} placeholder="km" value={z.km}
                      onChange={(e) => setZ({ km: e.target.value })} />
                    <span style={{ fontSize: '.78rem', color: 'var(--ink-dim)' }}>km</span>
                    <button type="button" title="Supprimer cette zone" onClick={() => setZones(zones.filter((_, j) => j !== i))}
                      style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.05rem' }}>🗑️</button>
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {miniField('Prix (€)', <input className="inp" inputMode="decimal" style={{ width: 78 }} value={z.fee} onChange={(e) => setZ({ fee: e.target.value })} />)}
                    {miniField('Min. commande (€)', <input className="inp" inputMode="decimal" style={{ width: 92 }} placeholder="—" value={z.min} onChange={(e) => setZ({ min: e.target.value })} />)}
                    {miniField('Gratuit dès (€)', <input className="inp" inputMode="decimal" style={{ width: 92 }} placeholder="—" value={z.free} onChange={(e) => setZ({ free: e.target.value })} />)}
                  </div>
                </div>
              );
            })}
            <button type="button" onClick={() => setZones([...zones, { km: '', fee: '' }])}
              style={{ marginTop: 4, padding: '8px 12px', borderRadius: 10, border: '1px dashed var(--gold-line)', background: 'var(--gold-soft)', color: 'var(--gold)', fontWeight: 800, fontSize: '.82rem', cursor: 'pointer' }}>
              ➕ Ajouter une zone
            </button>
          </div>)}

        <button className="btn-gold" style={{ marginTop: 6 }} disabled={saving} onClick={save}>💾 ENREGISTRER</button>
      </div>

      {/* Aperçu : tester une adresse client */}
      <div className="cc" style={{ maxWidth: 560 }}>
        <div className="staff-title" style={{ fontSize: '1.1rem', marginBottom: 8 }}>🔎 Tester une adresse client</div>
        <AddressAutocomplete placeholder="Tape une adresse de client…" value="" onPick={testAddress} />
        {test && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: test.eligible ? '#eafaf0' : '#fdecec', border: `1px solid ${test.eligible ? '#b6e6c9' : '#f2b8b8'}` }}>
            {test.eligible ? (
              <>
                <div style={{ fontWeight: 800, color: '#2a7' }}>✅ Livrable · {test.distanceKm} km{test.zoneLabel ? ` · ${test.zoneLabel}` : ''}</div>
                <div style={{ marginTop: 4 }}>Frais : <b>{test.feeCents ? euro(test.feeCents) : 'Gratuit'}</b>
                  {test.freeAboveCents ? ` (gratuit dès ${euro(test.freeAboveCents)})` : ''}</div>
                {test.minOrderCents > 0 && <div style={{ color: '#888', fontSize: '.85rem' }}>Minimum : {euro(test.minOrderCents)}</div>}
              </>
            ) : (
              <div style={{ fontWeight: 800, color: '#c33' }}>❌ {test.reason}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
